import { useApiDealsV1SellerTransactionHistoryRetrieve, useCurrentUser } from 'api';
import { useTranslation } from 'i18n';
import { Alert, Box, DataCard, formatNumber, Stack, Typography } from 'ui';
import { Campaign, Celebration, Restore } from 'ui/icons';

export const TransactionHistoryDataCard = ({ sellerId }: { sellerId: number }) => {
  const { t } = useTranslation('features');
  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const { data: totalTransactionHistory, isLoading: isTotalTransactionHistoryLoading } =
    useApiDealsV1SellerTransactionHistoryRetrieve(sellerId, undefined, {
      query: {
        enabled: Boolean(user),
        retry: false,
      },
    });
  const { data: buyerTransactionHistory, isLoading: isBuyerTransactionHistoryLoading } =
    useApiDealsV1SellerTransactionHistoryRetrieve(
      sellerId,
      {
        buyer_id: user?.id,
      },
      {
        query: {
          enabled: Boolean(user),
          retry: false,
        },
      }
    );

  const hasTransactions = Boolean(totalTransactionHistory);

  if (!hasTransactions) {
    return null;
  }

  const isLoading = isUserLoading || isTotalTransactionHistoryLoading || isBuyerTransactionHistoryLoading;

  const totalTransactions = {
    loads: totalTransactionHistory?.loads_count ?? 0,
    tonnes: totalTransactionHistory?.total_tonnes ? Number(totalTransactionHistory?.total_tonnes) : 0,
  };

  const buyerTransactions = {
    loads: buyerTransactionHistory?.loads_count ?? 0,
    tonnes: buyerTransactionHistory?.total_tonnes ? Number(buyerTransactionHistory?.total_tonnes) : 0,
  };
  const hasBuyerTransactions = buyerTransactions.loads > 0 || buyerTransactions.tonnes > 0;

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          avatar: <Restore />,
          title: t('dataCards.transactionHistory.title'),
        },
      }}
    >
      <Stack
        sx={{
          gap: 2,
          width: '100%',
        }}
      >
        {hasBuyerTransactions ? (
          <BuyerTransactions {...buyerTransactions} />
        ) : (
          <Alert severity="info" icon={<Campaign />}>
            {t('dataCards.transactionHistory.alerts.newSupplier')}
          </Alert>
        )}
        <TotalTransactions {...totalTransactions} />
      </Stack>
    </DataCard>
  );
};

const TotalTransactions = ({ tonnes, loads }: { tonnes: number; loads: number }) => {
  const { t } = useTranslation('features');

  return (
    <Stack
      direction="column"
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="body2" fontWeight="bold">
        {t('dataCards.transactionHistory.total.description')}
      </Typography>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'center',
        }}
      >
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(tonnes, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="total-tonnes"
        >
          {t('dataCards.common.metricTonnes')}
        </DataCard.Item>
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(loads, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="total-loads"
        >
          {t('dataCards.common.loads')}
        </DataCard.Item>
      </Stack>
    </Stack>
  );
};

const BuyerTransactions = ({ tonnes, loads }: { tonnes: number; loads: number }) => {
  const { t } = useTranslation('features');

  return (
    <Box
      sx={({ palette }) => ({
        backgroundColor: palette.primary[100],
        borderRadius: '8px',
        color: palette.primary.main,
      })}
    >
      <Alert severity="info" icon={<Celebration />}>
        {t('dataCards.transactionHistory.alerts.purchasedBefore')}
      </Alert>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'center',
        }}
      >
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(tonnes, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="buyer-tonnes"
        >
          {t('dataCards.common.metricTonnes')}
        </DataCard.Item>
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(loads, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="buyer-loads"
        >
          {t('dataCards.common.loads')}
        </DataCard.Item>
      </Stack>
    </Box>
  );
};
